import {
  Crosstab,
  CrosstabCreateArgs,
  CrosstabExportAsyncArgs,
  CrosstabExportDownloadArgs,
  CrosstabExportFilteredResults,
  CrosstabExportParams,
  CrosstabMultiScoreComparison,
  CrosstabMultiScoreComparisonRequestArgs,
  CrosstabSearchArgs,
  CrosstabSearchResult,
  CrosstabUpdateArgs,
} from "types";

import API from "api/client";
import { downloadFileWithoutPopup } from "utils/download/downloadFile";
import { envConfig } from "config";

const { crosstabAPI } = envConfig;

/**
 *
 * Returns a list of crosstabs based on search criteria.
 */
const getCrosstabs = async (params: CrosstabSearchArgs): Promise<CrosstabSearchResult> => {
  const { data } = await API.get<CrosstabSearchResult>(`${crosstabAPI}`, { params });
  return data;
};

/**
 * Fetch a Crosstab instance by ID.
 */
const getCrosstab = async (id: number): Promise<Crosstab> => {
  const { data } = await API.get<Crosstab>(`${crosstabAPI}${id}`);
  return data;
};

/**
 * Create a new Crosstab with the provided `CrosstabCreateArgs`.
 */
const createCrosstab = async (params: CrosstabCreateArgs): Promise<Crosstab> => {
  const { data } = await API.post<Crosstab>(crosstabAPI, params);
  return data;
};

/**
 * Updates a Crosstab with the provided `CrosstabUpdateArgs`.
 */
const updateCrosstab = async ({ crosstabId, ...params }: CrosstabUpdateArgs): Promise<Crosstab> => {
  const { data } = await API.put<Crosstab>(`${crosstabAPI}${crosstabId}`, params);
  return data;
};

/**
 * Deletes a Crosstab.
 */
const deleteCrosstab = async (crosstabId: number): Promise<void> => {
  await API.delete<Crosstab>(`${crosstabAPI}${crosstabId}`);
};

/**
 * Clone a Crosstab.
 */
const cloneCrosstab = async ({ crosstabId, ...params }: CrosstabUpdateArgs): Promise<Crosstab> => {
  const { data } = await API.post<Crosstab>(`${crosstabAPI}${crosstabId}/clone`, params);
  return data;
};

/**
 * Fetches data for a Crosstab MultiScore row comparison.
 */
const getMultiScoreRow = async (
  crosstabId: number,
  params: CrosstabMultiScoreComparisonRequestArgs,
): Promise<CrosstabMultiScoreComparison> => {
  const { data } = await API.get<CrosstabMultiScoreComparison>(`${crosstabAPI}${crosstabId}/multiscore-row`, {
    params,
  });
  return data;
};

/**
 * Downloads an already exported crosstab.
 */
const downloadCrosstab = async ({ crosstabId, exportId }: CrosstabExportDownloadArgs): Promise<void> => {
  const { data } = await API.get(`${crosstabAPI}${crosstabId}/download/${exportId}`);
  downloadFileWithoutPopup(data);
};

/**
 * Fetches a paginated list of exports for a crosstab.
 */
const getExports = async (crosstabId: number, params: CrosstabExportParams): Promise<CrosstabExportFilteredResults> => {
  const { data } = await API.get(`${crosstabAPI}${crosstabId}/exports`, { params });
  return data;
};

/**
 * Request to export a crosstab asynchronously.
 * This will return data about the export, but will always be in a "pending" state initially.
 */
const exportCrosstabAsync = async (crosstabId: number, params: CrosstabExportAsyncArgs): Promise<void> => {
  await API.post(`${crosstabAPI}${crosstabId}/export-new`, params);
};

export default {
  list: getCrosstabs,
  listExports: getExports,
  get: getCrosstab,
  create: createCrosstab,
  download: downloadCrosstab,
  update: updateCrosstab,
  delete: deleteCrosstab,
  exportAsync: exportCrosstabAsync,
  clone: cloneCrosstab,
  getMultiScoreRow,
};
